import {DataStatus} from 'platform/components';
import {Show, VStack} from 'platform/foundation';

import {useEffect, useReducer, useRef} from 'react';

import {add} from 'ramda';

import {useGetMetadaServiceCaseQuery, useGetServiceCaseOrdersQuery} from '@dms/api/workshop';
import {testIds} from '@dms/routes';
import {queryParams, Section} from '@dms/shared';

import {useQueryState} from 'shared/web';

import {useWorkshopUrl} from '../../../../hooks/useWorkshopUrl';
import {ServiceCaseOrder} from '../../../../types/OrderType';
import {AddLabourModal} from './components/AddLabourModal';
import {AddMaterialRequestModal} from './components/AddMaterialRequestModal';
import {OrderCard} from './components/OrderCard';

export function Orders() {
  const {serviceCaseId, addLabourJobId, addMaterialRequestJobId} = useWorkshopUrl();

  const [orderId, setOrderId, clearOrderId] = useQueryState(queryParams.SERVICE_CASE_ORDER_ID);

  const defaultOpenedRef = useRef(!!orderId);
  const [key, reloadComponents] = useReducer(add(1), 0);

  const {
    data: serviceCaseOrders,
    isLoading,
    isError,
  } = useGetServiceCaseOrdersQuery({serviceCaseId});
  const {data: serviceCase} = useGetMetadaServiceCaseQuery({serviceCaseId});

  useEffect(() => {
    const orderIdFirst = serviceCaseOrders?.[0]?.serviceOrderId;
    if (!defaultOpenedRef.current && !orderId && orderIdFirst) {
      defaultOpenedRef.current = true;
      if (serviceCaseOrders.length === 1) {
        setOrderId(orderIdFirst);
      }
    }
  }, [orderId, serviceCaseOrders, setOrderId]);

  const onCardExpand = (id: string) => {
    if (orderId === id) {
      clearOrderId();
    } else {
      setOrderId(id);
    }
  };

  const validOrders: ServiceCaseOrder[] =
    serviceCaseOrders?.filter((order) => order?.serviceOrderId) ?? [];

  return (
    <Section data-testid={testIds.workshop.serviceCaseDetail('orders')}>
      <DataStatus
        isLoading={isLoading}
        isError={isError}
        isEmpty={!validOrders.length}
        minHeight={64}
      >
        <VStack spacing={4}>
          {validOrders.map((order, index) => (
            <OrderCard
              key={order.serviceOrderId}
              defaultServiceOrder={order}
              serviceOrderId={order.serviceOrderId}
              serviceCase={serviceCase}
              isExpanded={orderId === order.serviceOrderId}
              dataGridKey={key}
              onExpand={() => onCardExpand(order.serviceOrderId)}
              onDataChange={reloadComponents}
              data-testid={testIds.workshop.serviceCaseDetail(`order-[${index}]`)}
            />
          ))}
        </VStack>
      </DataStatus>
      <Show when={!!addLabourJobId}>
        <AddLabourModal
          onSubmit={reloadComponents}
          data-testid={testIds.workshop.serviceCaseDetail('addWorkModal')}
        />
      </Show>
      <Show when={!!addMaterialRequestJobId}>
        <AddMaterialRequestModal
          onSubmit={reloadComponents}
          serviceCase={serviceCase}
          data-testid={testIds.workshop.serviceCaseDetail('addMaterialRequestModal')}
        />
      </Show>
    </Section>
  );
}
